import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { PrivacyMode, UserSettings, ServerDefaults } from "@edufadder/shared-types";
import { useAuth } from "../lib/auth-context.js";
import * as api from "../lib/api.js";
import { ApiError, type ResearchStatus } from "../lib/api.js";
import * as s from "../styles.js";

export function AccountPage(): JSX.Element {
  const { me, logout } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [serverDefaults, setServerDefaults] = useState<ServerDefaults | null>(null);
  const [research, setResearch] = useState<ResearchStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => {
    Promise.all([api.getMySettings(), api.getServerDefaults(), api.getResearchStatus()])
      .then(([s1, s2, s3]) => {
        setSettings(s1);
        setServerDefaults(s2);
        setResearch(s3);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : "Couldn't load your settings."));
  }, []);

  async function updatePrivacyMode(privacy_mode: PrivacyMode): Promise<void> {
    const updated = await api.patchMySettings({ privacy_mode });
    setSettings(updated);
  }

  async function toggleTopicContext(): Promise<void> {
    const updated = await api.patchMySettings({ show_topic_context: !settings?.show_topic_context });
    setSettings(updated);
  }

  async function toggleResearchConsent(): Promise<void> {
    if (research?.has_granted_consent) {
      await api.revokeResearchConsent();
    } else {
      await api.grantResearchConsent();
    }
    setResearch(await api.getResearchStatus());
  }

  async function handleDeleteAccount(): Promise<void> {
    await api.deleteMyAccount();
    await logout();
    navigate("/", { replace: true });
  }

  if (!me) return <div style={s.container} />;

  return (
    <div style={s.container}>
      <h1 style={s.heading1}>Your account</h1>
      <div style={s.card}>
        <p>
          <strong>{me.display_name}</strong> — {me.email}
        </p>
        <p style={s.mutedText}>Role: {me.role}</p>
        {me.orgs.length > 0 && (
          <p style={s.mutedText}>
            Organizations: {me.orgs.map((o) => `${o.name} (${o.role})`).join(", ")}
          </p>
        )}
      </div>

      {error && <p style={s.errorText}>{error}</p>}

      {settings && (
        <div style={s.card}>
          <h2 style={s.heading2}>How much of a page EduFadder analyzes</h2>
          <label style={{ display: "block", marginBottom: 6 }}>
            <input
              type="radio"
              checked={settings.privacy_mode === "minimal"}
              onChange={() => void updatePrivacyMode("minimal")}
            />{" "}
            Minimal — only the longest, most substantial passages
          </label>
          <label style={{ display: "block", marginBottom: 6 }}>
            <input
              type="radio"
              checked={settings.privacy_mode === "thorough"}
              onChange={() => void updatePrivacyMode("thorough")}
            />{" "}
            Thorough — every paragraph-like block on the page
          </label>
          <label style={{ display: "block", marginTop: 12 }}>
            <input type="checkbox" checked={settings.show_topic_context} onChange={() => void toggleTopicContext()} />{" "}
            Show context cards for related (not just endorsed) mentions of a topic
          </label>
          {serverDefaults && serverDefaults.source !== "account" && (
            <p style={{ ...s.mutedText, marginTop: 8 }}>
              Your organization's {serverDefaults.source === "institution" ? "institution-wide" : "platform"} default is
              at least as strict as this and always takes precedence where it's stricter.
            </p>
          )}
        </div>
      )}

      {research && (
        <div style={s.card}>
          <h2 style={s.heading2}>Research participation</h2>
          <p style={s.mutedText}>
            Research telemetry is only ever collected when three things are all true: the platform allows it, your
            organization (if any) has opted in, and you've personally consented — revocable at any time.
          </p>
          {!research.platform_permits_research_telemetry && (
            <p style={s.mutedText}>This EduFadder deployment doesn't have research telemetry enabled at all right now.</p>
          )}
          {research.platform_permits_research_telemetry && (
            <label style={{ display: "block", marginTop: 8 }}>
              <input type="checkbox" checked={research.has_granted_consent} onChange={() => void toggleResearchConsent()} />{" "}
              I consent to anonymized research telemetry
            </label>
          )}
          <p style={s.mutedText}>Currently active: {research.active ? "yes" : "no"}</p>
        </div>
      )}

      <ChangePassword />

      <div style={s.card}>
        <h2 style={s.heading2}>Delete account</h2>
        <p style={s.mutedText}>
          This deactivates your account and anonymizes your profile. Any evidence you authored or approved stays in
          the historical record (for audit integrity), but is no longer attributed to you by name.
        </p>
        {!confirmingDelete ? (
          <button type="button" style={s.buttonDanger} onClick={() => setConfirmingDelete(true)}>
            Delete my account
          </button>
        ) : (
          <>
            <p style={s.errorText}>Are you sure? This can't be undone.</p>
            <button type="button" style={s.buttonDanger} onClick={() => void handleDeleteAccount()}>
              Yes, delete my account
            </button>{" "}
            <button type="button" style={s.buttonSecondary} onClick={() => setConfirmingDelete(false)}>
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function ChangePassword(): JSX.Element {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setMessage(null);
    if (next !== confirm) {
      setMessage({ ok: false, text: "The two new passwords don't match." });
      return;
    }
    setBusy(true);
    try {
      await api.changeMyPassword(current, next);
      setCurrent("");
      setNext("");
      setConfirm("");
      setMessage({ ok: true, text: "Password changed. Your other sessions have been signed out." });
    } catch (err) {
      setMessage({ ok: false, text: err instanceof ApiError ? err.message : "Couldn't change your password." });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={s.card}>
      <h2 style={s.heading2}>Change password</h2>
      <p style={s.mutedText}>If an administrator created your account, replace the temporary password here.</p>
      <form onSubmit={(e) => void submit(e)} style={{ maxWidth: 360 }}>
        <label style={s.label}>
          Current password
          <input style={s.input} type="password" required autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
        </label>
        <label style={s.label}>
          New password (at least 10 characters)
          <input style={s.input} type="password" required minLength={10} autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />
        </label>
        <label style={s.label}>
          New password again
          <input style={s.input} type="password" required minLength={10} autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </label>
        <button type="submit" style={s.buttonPrimary} disabled={busy}>
          {busy ? "Changing…" : "Change password"}
        </button>
        {message && (
          <p role="status" style={message.ok ? { ...s.mutedText, color: "#2e7d4f" } : s.errorText}>
            {message.text}
          </p>
        )}
      </form>
    </div>
  );
}
